import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Button, Spinner, FormGroup, Label, Alert } from "reactstrap";
import Dropzone from "react-dropzone";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { bulkUploadStart } from "../../../../store/bulk-schedule/actions";
import { getOrgsAdmin } from "../../../../helpers/api_rombo_helper";
import { orgId } from "../../../../constants/utils";
import PlacesServices from "../../../Google/PlacesServices";
import { TypeAddress } from "../../../../constants/enums";
import i18n from "../../../../i18n";

const selectLogin = createSelector(
  (state) => state.Login,
  (l) => ({ token: l.token, data: l.data })
);

const selectBulk = createSelector(
  (state) => state.BulkSchedule,
  (bs) => ({ uploading: bs.uploading, error: bs.error })
);

const formatBytes = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const ACCEPTED = {
  "text/csv": [".csv"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"]
};

const StepUpload = () => {
  const dispatch = useDispatch();
  const { token, data } = useSelector(selectLogin);
  const { uploading, error } = useSelector(selectBulk);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState(null);

  const [selectedOrigin, setSelectedOrigin] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState({});
  const [addressesMarkers, setAddressesMarkers] = useState([]);
  const [onFocusAddress, setOnFocusAddress] = useState(null);

  const userProfile = {
    username: data?.data?.full_name || "",
    sub: data?.data?.id || ""
  };

  const userPartnerId = data?.data?.partner_id;

  useEffect(() => {
    if (!token) return;
    getOrgsAdmin(token, orgId)
      .then((res) => {
        const items = Array.isArray(res) ? res : res?.items ?? [];
        setCompanies(
          items.map((p) => ({
            value: p.partner_id,
            label: p.name
          }))
        );
      })
      .catch(() => {});
  }, [token]);

  const translatedErrors = useMemo(() => {
    if (!error?.detail) return null;

    const detail = error.detail;
    const parsed = [];

    if (detail.plates) {
      Object.entries(detail.plates).forEach(([plate]) => {
        parsed.push(`${i18n.t("InvalidPlate")}: ${plate}`);
      });
    }

    if (detail.dates) {
      Object.entries(detail.dates).forEach(([row, messages]) => {
        messages.forEach(() => {
          parsed.push(`${i18n.t("InvalidDateRow")} ${row}`);
        });
      });
    }

    if (detail.location) {
      Object.entries(detail.location).forEach(([row, messages]) => {
        messages.forEach((msg) => {
          if (msg.includes("latitude")) {
            parsed.push(`${i18n.t("InvalidLatitudeRow")} ${row}`);
          }

          if (msg.includes("longitude")) {
            parsed.push(`${i18n.t("InvalidLongitudeRow")} ${row}`);
          }
        });
      });
    }

    return parsed;
  }, [error]);

  const handleDrop = (accepted, rejected) => {
    setFileError(null);
    if (rejected.length > 0) {
      setFileError("Solo se aceptan archivos .csv o .xlsx (máx 10 MB).");
      return;
    }
    if (accepted.length > 0) {
      setSelectedFile(accepted[0]);
    }
  };

  const handleSubmit = () => {
    if (!selectedFile) {
      setFileError("Selecciona un archivo.");
      return;
    }

    const companyId = selectedCompany?.value ?? userPartnerId;
    const companyName = selectedCompany?.label;

    if (!companyId) {
      setFileError("Selecciona una empresa.");
      return;
    }

    if (!selectedAddress?.description) {
      setFileError("Selecciona una dirección de origen.");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    dispatch(
      bulkUploadStart(formData, {
        company_id: companyId,
        company_name: companyName,
        company_address: selectedAddress.description,
        latitude: selectedAddress.location.lat,
        longitude: selectedAddress.location.lon,
        created_by: userProfile.username || userProfile.sub
      })
    );
  };

  return (
    <div>
      <FormGroup>
        <Label className="request-create-title">{i18n.t("Partner")}</Label>
        <p className="request-create-text" htmlFor="companyInput">{i18n.t("SelectCompanyService")}</p>
        <Select
          options={companies}
          value={selectedCompany}
          onChange={setSelectedCompany}
          placeholder="Nombre de la empresa"
          isClearable
        />
      </FormGroup>
      <hr />
      <FormGroup className="mt-3">
        <Label className="request-create-title">
          {i18n.t("OriginAddress")}
        </Label>
        <p className="request-create-text">
          {i18n.t("TextAddressMassiveSchedule")}
        </p>
        <PlacesServices
          setSelectedOrigin={setSelectedOrigin}
          selectedOrigin={selectedOrigin}
          selectedAddress={selectedAddress}
          setSelectedAddress={setSelectedAddress}
          typeAddress={TypeAddress.ORIGIN}
          addressesMarkers={addressesMarkers}
          setAddressesMarkers={setAddressesMarkers}
          setOnFocusAddress={setOnFocusAddress}
          companie={selectedCompany?.value}
        />
      </FormGroup>
      <hr />
      <FormGroup className="mt-4">
        <Label className="request-create-title">
          {i18n.t("ServiceUploadFile")}
        </Label>
        <p className="request-create-text">
          {i18n.t("UploadYourFile")}
        </p>
        <Dropzone
          onDrop={handleDrop}
          accept={ACCEPTED}
          maxSize={10 * 1024 * 1024}
          multiple={false}
        >
          {({ getRootProps, getInputProps, isDragActive }) => (
            <div
              {...getRootProps()}
              style={{
                border: `2px dashed ${isDragActive ? "#556ee6" : "#ced4da"}`,
                borderRadius: 8,
                padding: "32px 16px",
                textAlign: "center",
                cursor: "pointer",
                background: isDragActive ? "#f0f2ff" : "#fafafa",
                transition: "all 0.2s"
              }}
            >
              <input {...getInputProps()} />
              {selectedFile ? (
                <div>
                  <i
                    className="mdi mdi-file-excel-outline"
                    style={{ fontSize: 32, color: "#34c38f" }}
                  />
                  <p className="mb-0 mt-2" style={{ fontWeight: 600 }}>
                    {selectedFile.name}
                  </p>
                  <p className="text-muted" style={{ fontSize: 12 }}>
                    {formatBytes(selectedFile.size)}
                  </p>
                  <Button
                    size="sm"
                    color="link"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                  >
                    Cambiar archivo
                  </Button>
                </div>
              ) : (
                <div>
                  <i className="mdi mdi-cloud-upload-outline" style={{ fontSize: 36, color: "#adb5bd" }} />
                  <p className="mb-1 mt-2">Arrastra tu archivo aquí o haz clic para seleccionarlo</p>
                  <p className="text-muted" style={{ fontSize: 12 }}>CSV o XLSX, máx 10 MB</p>
                </div>
              )}
            </div>
          )}
        </Dropzone>
        {fileError && (
          <p className="text-danger mt-1" style={{ fontSize: 13 }}>
            {fileError}
          </p>
        )}
      </FormGroup>
      {translatedErrors?.length > 0 && (
        <Alert color="danger" className="mt-3">
          <ul className="mb-0 ps-3">
            {translatedErrors.map((err, index) => (
              <li key={index}>{err}</li>
            ))}
          </ul>
        </Alert>
      )}
      <Row className="mt-4">
        <Col className="text-end">
          <Button
            className="btn btn-request"
            onClick={handleSubmit}
            disabled={uploading || !selectedFile}
          >
            {uploading ? <Spinner size="sm" className="me-1" /> : null}
            {uploading ? "Subiendo..." : "Cargar archivo"}
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default StepUpload;
